import React, { useState } from 'react';
import FileUpload from '../components/FileUpload';
import DocumentTypeSelector from '../components/DocumentTypeSelector';
import ParserModeSelector from '../components/ParserModeSelector';
import EditableDataPreview from '../components/EditableDataPreview';
import ConversionStatus from '../components/ConversionStatus';
import { Shield, FileSpreadsheet, Download, Trash2, CheckCircle2, Camera, Sparkles, FileText, Zap, LogOut, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { user, logout } = useAuth();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [documentType, setDocumentType] = useState('auto');
  const [parserMode, setParserMode] = useState('standard'); // standard or ai
  const [conversionStatus, setConversionStatus] = useState('idle'); // idle, processing, completed, failed
  const [previewData, setPreviewData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [error, setError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleFilesUploaded = async (files) => {
    try {
      setIsAnimating(true);
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      if (response.data.success) {
        setUploadedFiles(response.data.files);
        setJobId(response.data.job_id);
        toast.success(`🎉 ${response.data.files_uploaded} file(s) uploaded successfully`);
      }
      setIsAnimating(false);
    } catch (err) {
      console.error('Upload error:', err);
      setIsAnimating(false);
      
      // Handle password-protected PDFs
      if (err.response?.data?.detail?.includes('password protected')) {
        toast.error('🔒 PDF is password protected. Please unlock the file and try again.');
      } else if (err.response?.data?.detail?.includes('Invalid file type')) {
        toast.error('⚠️ Invalid file type. Please upload PDF or image files (JPG, PNG, etc.)');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to upload files');
      }
      setError(err.response?.data?.detail || 'Failed to upload files');
    }
  };

  const handleConvert = async () => {
    if (!jobId) {
      toast.error('Please upload files first');
      return;
    }

    setConversionStatus('processing');
    setError(null);
    setIsAnimating(true);

    try {
      const response = await axios.post(`${API}/convert`, {
        job_id: jobId,
        document_type: documentType,
        parser_mode: parserMode,
      }, {
        withCredentials: true,
      });

      if (response.data.success) {
        setConversionStatus('completed');
        setPreviewData(response.data.preview_data);
        
        // Show detected bank if available
        const detectedBank = response.data.preview_data?.detected_bank;
        if (detectedBank && detectedBank !== 'unknown') {
          toast.success(`✨ Conversion completed! Detected bank: ${detectedBank.toUpperCase()}`);
        } else {
          toast.success('✨ Conversion completed successfully!');
        }
      }
      setIsAnimating(false);
    } catch (err) {
      console.error('Conversion error:', err);
      setConversionStatus('failed');
      setIsAnimating(false);
      
      // Better error messages
      let errorMessage = 'Conversion failed';
      if (err.response?.data?.detail?.includes('Could not extract meaningful text')) {
        errorMessage = '📸 Image quality too low. Please upload a clearer image.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleDataUpdate = (updatedData) => {
    setEditedData(updatedData);
  };

  const handleDownload = async () => {
    if (!jobId) return;

    try {
      const response = await axios.get(`${API}/download/${jobId}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `converted_${documentType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('📥 Excel file downloaded successfully');
    } catch (err) {
      console.error('Download error:', err);
      toast.error(err.response?.data?.detail || 'Failed to download file');
    }
  };

  const handleCleanup = async () => {
    if (!jobId) return;

    try {
      await axios.delete(`${API}/cleanup/${jobId}`);
      
      // Reset state
      setUploadedFiles([]);
      setJobId(null);
      setConversionStatus('idle');
      setPreviewData(null);
      setEditedData(null);
      setError(null);
      
      toast.success('🗑️ Files deleted successfully');
    } catch (err) {
      console.error('Cleanup error:', err);
      toast.error('Failed to delete files');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-cyan-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-emerald-200 sticky top-0 z-50 shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 group">
              <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 p-2.5 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <FileSpreadsheet className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-700 to-teal-700 bg-clip-text text-transparent">
                  Payslip<span className="font-normal">2</span>Excel
                </h1>
                <p className="text-sm text-slate-600 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-emerald-600" />
                  AI-Powered Document Converter
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4">
              {/* User Info */}
              {user && (
                <>
                  <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-semibold text-emerald-800">
                      {user.conversions_remaining} conversions left
                    </span>
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <span className="hidden sm:block text-sm font-medium text-slate-700">{user.name}</span>
                  </div>
                  <Button
                    onClick={logout}
                    variant="outline"
                    className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 transition-all duration-200"
                  >
                    <LogOut className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Logout</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Info Banner */}
        <div className="mb-8 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-200 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-start space-x-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-emerald-900 mb-2 text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Enhanced AI Processing with Photo Support!
              </h3>
              <div className="grid sm:grid-cols-2 gap-3 text-sm text-emerald-800">
                <div className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold mt-0.5">✓</span>
                  <span>Upload PDFs <strong>OR</strong> photos of payslips, bank statements & utility bills</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold mt-0.5">✓</span>
                  <span>Smart auto-detection for UK banks & utility providers</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold mt-0.5">✓</span>
                  <span>Edit uncertain fields with our interactive preview</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold mt-0.5">✓</span>
                  <span>Professional Excel with summary sheets & charts</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-3xl">
          <div className="p-6 sm:p-8">
            {/* Step 1: Upload */}
            <div className="mb-8 transition-all duration-500 ease-in-out">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  uploadedFiles.length > 0 ? 'bg-emerald-500 shadow-lg' : 'bg-emerald-100'
                }`}>
                  <span className={`font-bold transition-all duration-300 ${
                    uploadedFiles.length > 0 ? 'text-white' : 'text-emerald-700'
                  }`}>1</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Upload Documents or Images</h2>
                {uploadedFiles.length > 0 && (
                  <Sparkles className="w-5 h-5 text-emerald-600 animate-pulse" />
                )}
              </div>
              <FileUpload onFilesUploaded={handleFilesUploaded} disabled={conversionStatus === 'processing'} />
              
              {uploadedFiles.length > 0 && (
                <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl animate-fadeIn shadow-md">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <p className="text-sm font-semibold text-emerald-900">
                      ✓ {uploadedFiles.length} file(s) uploaded: {uploadedFiles.slice(0, 3).join(', ')}
                      {uploadedFiles.length > 3 && ` and ${uploadedFiles.length - 3} more...`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Select Document Type */}
            {jobId && (
              <div className="mb-8 animate-fadeIn transition-all duration-500">
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    documentType ? 'bg-emerald-500 shadow-lg' : 'bg-emerald-100'
                  }`}>
                    <span className={`font-bold transition-all duration-300 ${
                      documentType ? 'text-white' : 'text-emerald-700'
                    }`}>2</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Select Document Type</h2>
                </div>
                <DocumentTypeSelector 
                  value={documentType} 
                  onChange={setDocumentType}
                  disabled={conversionStatus === 'processing'}
                />
              </div>
            )}

            {/* Step 2.5: Select Parser Mode */}
            {jobId && (
              <div className="mb-8 animate-fadeIn transition-all duration-500">
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    parserMode ? 'bg-emerald-500 shadow-lg' : 'bg-emerald-100'
                  }`}>
                    <span className={`font-bold transition-all duration-300 ${
                      parserMode ? 'text-white' : 'text-emerald-700'
                    }`}>2.5</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Choose Parser Method</h2>
                </div>
                <ParserModeSelector 
                  value={parserMode} 
                  onChange={setParserMode}
                  disabled={conversionStatus === 'processing'}
                />
              </div>
            )}

            {/* Step 3: Convert */}
            {jobId && (
              <div className="mb-8 animate-fadeIn transition-all duration-500">
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    conversionStatus === 'completed' ? 'bg-emerald-500 shadow-lg' : 'bg-emerald-100'
                  }`}>
                    <span className={`font-bold transition-all duration-300 ${
                      conversionStatus === 'completed' ? 'text-white' : 'text-emerald-700'
                    }`}>3</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Convert to Excel</h2>
                </div>
                <Button
                  data-testid="convert-button"
                  onClick={handleConvert}
                  disabled={conversionStatus === 'processing'}
                  className="w-full sm:w-auto bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 text-white px-8 py-4 rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {conversionStatus === 'processing' ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                      Converting Magic...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Convert to Excel
                    </span>
                  )}
                </Button>
              </div>
            )}

            {/* Conversion Status */}
            {conversionStatus !== 'idle' && (
              <div className="animate-fadeIn">
                <ConversionStatus status={conversionStatus} error={error} />
              </div>
            )}

            {/* Preview */}
            {previewData && conversionStatus === 'completed' && (
              <div className="mb-8 animate-fadeIn">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 shadow-lg flex items-center justify-center">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Review & Edit (Optional)</h2>
                  {previewData.detected_bank && previewData.detected_bank !== 'unknown' && (
                    <span className="px-3 py-1 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 rounded-full text-sm font-bold border-2 border-emerald-300 animate-pulse">
                      🏦 Detected: {previewData.detected_bank.toUpperCase()}
                    </span>
                  )}
                </div>
                <EditableDataPreview data={previewData} onDataUpdate={handleDataUpdate} />
              </div>
            )}

            {/* Actions */}
            {conversionStatus === 'completed' && (
              <div className="flex flex-wrap gap-4 animate-fadeIn">
                <Button
                  data-testid="download-button"
                  onClick={handleDownload}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Excel
                </Button>
                <Button
                  data-testid="delete-button"
                  onClick={handleCleanup}
                  variant="outline"
                  className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 px-8 py-4 rounded-xl font-bold transition-all duration-300 transform hover:-translate-y-1"
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  Delete Files
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-purple-200 hover:border-purple-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2 group">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
              <Camera className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2 text-lg">Photo & Screenshot Support</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Take a photo or screenshot of your documents and convert instantly. Supports JPG, PNG, and more with advanced OCR.
            </p>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-emerald-200 hover:border-emerald-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2 group">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
              <Zap className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2 text-lg">Smart AI Detection</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Enhanced AI auto-detects Barclays, HSBC, Lloyds, NatWest, Monzo and more with bank-specific parsing for 99% accuracy.
            </p>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-cyan-200 hover:border-cyan-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2 group">
            <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
              <FileSpreadsheet className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2 text-lg">Professional Excel Output</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Multi-sheet Excel with summary, spending analysis, YTD totals, and professional formatting. Editable preview included.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8 text-center text-sm text-slate-600 border-t border-slate-200 bg-white/50 backdrop-blur-sm relative z-10">
        <p className="font-medium">&copy; 2025 Payslip2Excel. AI-Powered Privacy-First Document Processing.</p>
        <p className="mt-2 text-xs text-slate-500 flex items-center justify-center gap-2">
          <Sparkles className="w-3 h-3 text-emerald-600" />
          Enhanced Accuracy • Photo Support • Smarter AI Features
        </p>
      </footer>
    </div>
  );
};

export default HomePage;
